import React, { useCallback, useMemo, useRef } from 'react';
import { Button, ConfigProvider, Form } from 'antd';
import { IMetricEventsProps } from '../../typed';
import { GetMetric } from './GetMetric';
import style from './style.less';
import { MetricDataScope } from '@src/emp-3.0/types';
import { GetMetricProps, RatioMetricInfo } from './service';
import { useEventListForCreateMetric, useGetCalculableMetrics } from '@src/emp-3.0/api/queryHooks';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';

const RatioMetricFormular: React.FC<IMetricEventsProps & { metricInfo?: RatioMetricInfo }> = ({
  form,
  isView,
  metricInfo,
}) => {
  const { setActiveTool } = useFlowStore();
  const numerator = Form.useWatch<GetMetricProps>(['definition', 'numerator'], form);
  const denominator = Form.useWatch<GetMetricProps>(['definition', 'denominator'], form);
  const numeratorEventId = Form.useWatch(['definition', 'numerator', 'event'], form);
  const denominatorEventId = Form.useWatch(['definition', 'denominator', 'event'], form);

  const { data: calculableMetrics = [] } = useGetCalculableMetrics();
  const { data: eventList = [] } = useEventListForCreateMetric();
  const getDataScope = useCallback(
    (info?: GetMetricProps) => {
      const { used_metric_id, event } = info ?? {};
      if (used_metric_id && Array.isArray(calculableMetrics)) {
        const metric = calculableMetrics.find((it) => it.id === used_metric_id);
        if (metric) {
          return metric.target_scope;
        }
      }
      if (event && Array.isArray(eventList)) {
        const target = eventList.find((it) => it.id === event || it.name === event);
        if (target) {
          if (target.name === 'conviva_network_request') {
            return ['service_flow'] as MetricDataScope[];
          } else {
            return ['event_summary'] as MetricDataScope[];
          }
        }
      }
      return undefined;
    },
    [calculableMetrics, eventList],
  );
  const previousMetrics = useRef<{ numerator: GetMetricProps; denominator: GetMetricProps }>();
  const dataScope: {
    numerator: MetricDataScope[] | undefined;
    denominator: MetricDataScope[] | undefined;
  } = useMemo(() => {
    let numeratorScope = getDataScope(denominator);
    let denominatorScope = getDataScope(numerator);
    if (
      Array.isArray(numeratorScope) &&
      Array.isArray(denominatorScope) &&
      previousMetrics.current &&
      numeratorScope.every((it) => !(denominatorScope as MetricDataScope[]).includes(it))
    ) {
      const numeratorChanged = previousMetrics.current.numerator !== numerator;
      const denominatorChanged = previousMetrics.current.denominator !== denominator;
      if (numeratorChanged !== denominatorChanged) {
        if (numeratorChanged) {
          denominatorScope = undefined;
        } else {
          numeratorScope = undefined;
        }
      }
    }
    const result = {
      numerator: numeratorScope,
      denominator: denominatorScope,
    };
    previousMetrics.current = {
      denominator,
      numerator,
    };
    return result;
  }, [getDataScope, numerator, denominator]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Radio: {
            buttonColor: 'rgba(0, 0, 0, 0.45)',
          },
        },
      }}
    >
      <div
        style={{
          marginBottom: '12px',
        }}
      >
        <div className={style.metricRoleLabel}>Numerator</div>
        <GetMetric
          form={form}
          metricRole="numerator"
          isView={isView}
          dataScope={dataScope.numerator}
          metricInfo={metricInfo}
        ></GetMetric>
      </div>
      <div
        style={{
          marginBottom: '12px',
        }}
      >
        <div className={style.metricRoleLabel}>Denominator</div>
        <GetMetric
          form={form}
          metricRole="denominator"
          isView={isView}
          dataScope={dataScope.denominator}
          metricInfo={metricInfo}
        ></GetMetric>
      </div>
      {!isView && (numeratorEventId || denominatorEventId) ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type={'link'}
            onClick={() => {
              setActiveTool('validation');
            }}
          >
            Validate Event Coverage
          </Button>
        </div>
      ) : null}
    </ConfigProvider>
  );
};

export default RatioMetricFormular;
